// overdue-by-employee-chart.tsx — Overdue tickets by employee bar chart.
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import type { OverdueTicketsByEmployee } from '@/api/tickets-statistics';

interface Props {
  data: OverdueTicketsByEmployee[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function OverdueByEmployeeChart({ data, isLoading, isError }: Props) {
  const rows = (data ?? [])
    .filter((d) => d.overdue_tickets > 0)
    .sort((a, b) => b.overdue_tickets - a.overdue_tickets)
    .slice(0, 15)
    .map((d) => ({
      label: d.employee_name,
      overdue: d.overdue_tickets,
      ontime: d.ontime_tickets,
    }));

  const series = [
    { key: 'overdue', label: 'Overdue', color: CHART_COLORS[4] },   // red
    { key: 'ontime', label: 'On-time', color: CHART_COLORS[2] },    // green
  ];

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>Overdue Tickets by Employee</CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load overdue data" />
        ) : isLoading ? (
          <Skeleton style={{ height: 260 }} />
        ) : rows.length === 0 ? (
          <EmptyState title="No overdue tickets" description="No overdue tickets found in this date range." />
        ) : (
          <Chart
            type="bar"
            data={rows}
            xKey="label"
            series={series}
            height={260}
            showGrid
            showXAxis
            showYAxis
            valueFormatter={(v) => String(v ?? 0)}
          />
        )}
      </CardBody>
    </Card>
  );
}
